/**
 * Centralized SessionStorage Service Module
 * Encapsulates all sessionStorage access with consistent CRUD operations
 * Updated for new JWT-based authentication with access and refresh tokens
 */

import logger from '../utils/logger';

/**
 * Get access token from sessionStorage
 * @returns {string|null} Access token or null if not found
 */
export const getAccessToken = () => {
  try {
    return sessionStorage.getItem('accessToken');
  } catch (error) {
    logger.error('Failed to get access token', { error: error.message });
    return null;
  }
};

/**
 * Set access token in sessionStorage
 * @param {string} token - Access token to store
 */
export const setAccessToken = (token) => {
  try {
    if (token) {
      sessionStorage.setItem('accessToken', token);
    } else {
      sessionStorage.removeItem('accessToken');
    }
  } catch (error) {
    logger.error('Failed to set access token', { error: error.message });
  }
};

/**
 * Remove access token from sessionStorage
 */
export const removeAccessToken = () => {
  try {
    sessionStorage.removeItem('accessToken');
  } catch (error) {
    logger.error('Failed to remove access token', { error: error.message });
  }
};

/**
 * Get refresh token from sessionStorage
 * @returns {string|null} Refresh token or null if not found
 */
export const getRefreshToken = () => {
  try {
    return sessionStorage.getItem('refreshToken');
  } catch (error) {
    logger.error('Failed to get refresh token', { error: error.message });
    return null;
  }
};

/**
 * Set refresh token in sessionStorage
 * @param {string} token - Refresh token to store
 */
export const setRefreshToken = (token) => {
  try {
    if (token) {
      sessionStorage.setItem('refreshToken', token);
    } else {
      sessionStorage.removeItem('refreshToken');
    }
  } catch (error) {
    logger.error('Failed to set refresh token', { error: error.message });
  }
};

/**
 * Remove refresh token from sessionStorage
 */
export const removeRefreshToken = () => {
  try {
    sessionStorage.removeItem('refreshToken');
  } catch (error) {
    logger.error('Failed to remove refresh token', { error: error.message });
  }
};

// Legacy support for components that still use getSessionToken
/**
 * @deprecated Use getAccessToken instead
 */
export const getSessionToken = () => {
  return getAccessToken();
};

/**
 * @deprecated Use setAccessToken instead
 */
export const setSessionToken = (token) => {
  setAccessToken(token);
};

/**
 * @deprecated Use removeAccessToken instead
 */
export const removeSessionToken = () => {
  removeAccessToken();
};

/**
 * Get user information from sessionStorage
 * @returns {Object|null} User object or null if not found
 */
export const getUserInfo = () => {
  try {
    const stored = sessionStorage.getItem('userInfo');
    if (stored) {
      return JSON.parse(stored);
    }
    return null;
  } catch (error) {
    logger.error('Failed to parse stored user info', { error: error.message });
    sessionStorage.removeItem('userInfo');
    return null;
  }
};

/**
 * Set user information in sessionStorage
 * @param {Object} userInfo - User information object to store
 */
export const setUserInfo = (userInfo) => {
  try {
    if (userInfo) {
      sessionStorage.setItem('userInfo', JSON.stringify(userInfo));
    } else {
      sessionStorage.removeItem('userInfo');
    }
  } catch (error) {
    logger.error('Failed to set user info', { error: error.message });
  }
};

/**
 * Remove user information from sessionStorage
 */
export const removeUserInfo = () => {
  try {
    sessionStorage.removeItem('userInfo');
  } catch (error) {
    logger.error('Failed to remove user info', { error: error.message });
  }
};

// Legacy support for components that still use getUserId
/**
 * Get user ID from sessionStorage (legacy support)
 * @returns {string|null} User ID or null if not found
 */
export const getUserId = () => {
  try {
    const userInfo = getUserInfo();
    return userInfo ? userInfo.user_id : null;
  } catch (error) {
    logger.error('Failed to get user ID', { error: error.message });
    return null;
  }
};

/**
 * Set user ID in sessionStorage (legacy support)
 * @param {string} userId - User ID to store
 */
export const setUserId = (userId) => {
  try {
    const userInfo = getUserInfo() || {};
    userInfo.user_id = userId;
    setUserInfo(userInfo);
  } catch (error) {
    logger.error('Failed to set user ID', { error: error.message });
  }
};

/**
 * Remove user ID from sessionStorage (legacy support)
 */
export const removeUserId = () => {
  try {
    removeUserInfo();
  } catch (error) {
    logger.error('Failed to remove user ID', { error: error.message });
  }
};

/**
 * Get chat history from sessionStorage
 * @returns {Array} Chat history array or empty array if not found/corrupted
 */
export const getChatHistory = () => {
  try {
    const stored = sessionStorage.getItem('chatHistory');
    if (stored) {
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    }
    return [];
  } catch (error) {
    logger.error('Failed to parse stored chat history', { error: error.message });
    sessionStorage.removeItem('chatHistory');
    return [];
  }
};

/**
 * Set chat history in sessionStorage
 * @param {Array} history - Chat history array to store
 */
export const setChatHistory = (history) => {
  try {
    if (Array.isArray(history) && history.length > 0) {
      sessionStorage.setItem('chatHistory', JSON.stringify(history));
    } else {
      sessionStorage.removeItem('chatHistory');
    }
  } catch (error) {
    logger.error('Failed to set chat history', { error: error.message });
  }
};

/**
 * Remove chat history from sessionStorage
 */
export const removeChatHistory = () => {
  try {
    sessionStorage.removeItem('chatHistory');
  } catch (error) {
    logger.error('Failed to remove chat history', { error: error.message });
  }
};

/**
 * Get system prompt from sessionStorage
 * @returns {Object|null} System prompt object or null if not found
 */
export const getSystemPrompt = () => {
  try {
    const stored = sessionStorage.getItem('systemPrompt');
    if (stored) {
      return JSON.parse(stored);
    }
    return null;
  } catch (error) {
    logger.error('Failed to parse stored system prompt', { error: error.message });
    sessionStorage.removeItem('systemPrompt');
    return null;
  }
};

/**
 * Set system prompt in sessionStorage
 * @param {Object} systemPrompt - System prompt object to store
 */
export const setSystemPrompt = (systemPrompt) => {
  try {
    if (systemPrompt) {
      sessionStorage.setItem('systemPrompt', JSON.stringify(systemPrompt));
    } else {
      sessionStorage.removeItem('systemPrompt');
    }
  } catch (error) {
    logger.error('Failed to set system prompt', { error: error.message });
  }
};

/**
 * Remove system prompt from sessionStorage
 */
export const removeSystemPrompt = () => {
  try {
    sessionStorage.removeItem('systemPrompt');
  } catch (error) {
    logger.error('Failed to remove system prompt', { error: error.message });
  }
};

/**
 * Get dynamic system prompt from sessionStorage
 * @returns {Object|null} Dynamic system prompt object or null if not found
 */
export const getDynamicSystemPrompt = () => {
  try {
    const stored = sessionStorage.getItem('dynamicSystemPrompt');
    if (stored) {
      return JSON.parse(stored);
    }
    return null;
  } catch (error) {
    logger.error('Failed to parse stored dynamic system prompt', { error: error.message });
    sessionStorage.removeItem('dynamicSystemPrompt');
    return null;
  }
};

/**
 * Set dynamic system prompt in sessionStorage
 * @param {Object} dynamicSystemPrompt - Dynamic system prompt object to store
 */
export const setDynamicSystemPrompt = (dynamicSystemPrompt) => {
  try {
    if (dynamicSystemPrompt) {
      sessionStorage.setItem('dynamicSystemPrompt', JSON.stringify(dynamicSystemPrompt));
    } else {
      sessionStorage.removeItem('dynamicSystemPrompt');
    }
  } catch (error) {
    logger.error('Failed to set dynamic system prompt', { error: error.message });
  }
};

/**
 * Remove dynamic system prompt from sessionStorage
 */
export const removeDynamicSystemPrompt = () => {
  try {
    sessionStorage.removeItem('dynamicSystemPrompt');
  } catch (error) {
    logger.error('Failed to remove dynamic system prompt', { error: error.message });
  }
};

/**
 * Get conversation ID from sessionStorage
 * @returns {string|null} Conversation ID or null if not found
 */
export const getConversationId = () => {
  try {
    return sessionStorage.getItem('conversationId');
  } catch (error) {
    logger.error('Failed to get conversation ID', { error: error.message });
    return null;
  }
};

/**
 * Set conversation ID in sessionStorage
 * @param {string} conversationId - Conversation ID to store
 */
export const setConversationId = (conversationId) => {
  try {
    if (conversationId) {
      sessionStorage.setItem('conversationId', conversationId);
    } else {
      sessionStorage.removeItem('conversationId');
    }
  } catch (error) {
    logger.error('Failed to set conversation ID', { error: error.message });
  }
};

/**
 * Remove conversation ID from sessionStorage
 */
export const removeConversationId = () => {
  try {
    sessionStorage.removeItem('conversationId');
  } catch (error) {
    logger.error('Failed to remove conversation ID', { error: error.message });
  }
};

// ===== SAVED CHAT FUNCTIONS =====

/**
 * Get saved chats list from sessionStorage
 * @returns {Array} Array of saved chat summaries or empty array
 */
export const getSavedChatsList = () => {
  try {
    const savedChats = sessionStorage.getItem('saved_chats_list');
    if (!savedChats || savedChats === 'null') {
      return [];
    }
    const parsed = JSON.parse(savedChats);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    logger.error('Failed to get saved chats list', { error: error.message });
    return [];
  }
};

/**
 * Set saved chats list in sessionStorage
 * @param {Array} savedChats - Array of saved chat summaries to store
 */
export const setSavedChatsList = (savedChats) => {
  try {
    if (Array.isArray(savedChats)) {
      sessionStorage.setItem('saved_chats_list', JSON.stringify(savedChats));
    } else {
      sessionStorage.removeItem('saved_chats_list');
    }
  } catch (error) {
    logger.error('Failed to set saved chats list', { error: error.message });
  }
};

/**
 * Remove saved chats list from sessionStorage
 */
export const removeSavedChatsList = () => {
  try {
    sessionStorage.removeItem('saved_chats_list');
  } catch (error) {
    logger.error('Failed to remove saved chats list', { error: error.message });
  }
};

/**
 * Clear all session data from sessionStorage
 */
export const clearAll = () => {
  try {
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('userInfo');
    sessionStorage.removeItem('chatHistory');
    sessionStorage.removeItem('systemPrompt');
    sessionStorage.removeItem('dynamicSystemPrompt');
    sessionStorage.removeItem('conversationId');
    sessionStorage.removeItem('currentSubject');
    sessionStorage.removeItem('subjectPromptText');
    sessionStorage.removeItem('enable_current_subject_tracking');
    sessionStorage.removeItem('lock_current_subject');
    sessionStorage.removeItem('saved_chats_list');
    sessionStorage.removeItem('jj_saved_chats_top');
    sessionStorage.removeItem('jj_saved_chats_needs_init');
    sessionStorage.removeItem('jj_session_id');
    sessionStorage.removeItem('jj_chat_message_count');
    // Profile data cleanup
    sessionStorage.removeItem('profile_facts_firstname');
    sessionStorage.removeItem('profile_facts_lastname');
    sessionStorage.removeItem('profile_facts_gender');
    sessionStorage.removeItem('profile_facts_form_of_address');
    sessionStorage.removeItem('profile_facts_personal_info');
    sessionStorage.removeItem('profile_facts_learned_information');
    sessionStorage.removeItem('profile_unsaved_changes');
    sessionStorage.removeItem('profile_editing_fact_id');
    // Legacy cleanup
    sessionStorage.removeItem('sessionToken');
    sessionStorage.removeItem('userId');
  } catch (error) {
    logger.error('Failed to clear all session data', { error: error.message });
  }
};

/**
 * List all current sessionStorage keys
 * @returns {Array} Array of current session keys
 */
export const listAllKeys = () => {
  try {
    return Object.keys(sessionStorage);
  } catch (error) {
    logger.error('Failed to list session keys', { error: error.message });
    return [];
  }
};

/**
 * Check if a specific key exists in sessionStorage
 * @param {string} key - Key to check
 * @returns {boolean} True if key exists, false otherwise
 */
export const hasKey = (key) => {
  try {
    return sessionStorage.getItem(key) !== null;
  } catch (error) {
    logger.error('Failed to check key existence', { error: error.message });
    return false;
  }
};

// ===== SAVED CHATS AREA (RSCAF2) =====
/**
 * Get the top offset (in px) for the Saved Chats area separator bar
 * @returns {number|null} number of pixels from the top of the sidebar, or null if not set
 */
export const getSavedChatsAreaTop = () => {
  try {
    const value = sessionStorage.getItem('jj_saved_chats_top');
    if (value === null || value === undefined) return null;
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  } catch (error) {
    logger.error('Failed to get saved chats area top', { error: error.message });
    return null;
  }
};

/**
 * Persist the top offset (in px) for the Saved Chats area separator bar
 * @param {number} topPx
 */
export const setSavedChatsAreaTop = (topPx) => {
  try {
    if (Number.isFinite(topPx)) {
      sessionStorage.setItem('jj_saved_chats_top', String(Math.round(topPx)));
    } else {
      sessionStorage.removeItem('jj_saved_chats_top');
    }
  } catch (error) {
    logger.error('Failed to set saved chats area top', { error: error.message });
  }
};

/**
 * Remove the saved chats area top offset
 */
export const removeSavedChatsAreaTop = () => {
  try {
    sessionStorage.removeItem('jj_saved_chats_top');
  } catch (error) {
    logger.error('Failed to remove saved chats area top', { error: error.message });
  }
};

/**
 * RSCAF sentinel helpers
 */
export const getSavedChatsNeedsInit = () => {
  try {
    return sessionStorage.getItem('jj_saved_chats_needs_init') === '1';
  } catch (error) {
    logger.error('Failed to get saved chats needs init flag', { error: error.message });
    return false;
  }
};

export const setSavedChatsNeedsInit = (needsInit) => {
  try {
    sessionStorage.setItem('jj_saved_chats_needs_init', needsInit ? '1' : '0');
  } catch (error) {
    logger.error('Failed to set saved chats needs init flag', { error: error.message });
  }
};

export const removeSavedChatsNeedsInit = () => {
  try {
    sessionStorage.removeItem('jj_saved_chats_needs_init');
  } catch (error) {
    logger.error('Failed to remove saved chats needs init flag', { error: error.message });
  }
};

// ===== SESSION STATS HELPERS =====
/**
 * Get the per-tab session identifier
 * @returns {string|null}
 */
export const getSessionId = () => {
  try {
    return sessionStorage.getItem('jj_session_id');
  } catch (error) {
    logger.error('Failed to get session id', { error: error.message });
    return null;
  }
};

/**
 * Set the per-tab session identifier
 * @param {string|null} value
 */
export const setSessionId = (value) => {
  try {
    if (value) {
      sessionStorage.setItem('jj_session_id', value);
    } else {
      sessionStorage.removeItem('jj_session_id');
    }
  } catch (error) {
    logger.error('Failed to set session id', { error: error.message });
  }
};

/**
 * Clear the session identifier
 */
export const clearSessionId = () => {
  try {
    sessionStorage.removeItem('jj_session_id');
  } catch (error) {
    logger.error('Failed to clear session id', { error: error.message });
  }
};

/**
 * Get the current chat message counter
 * @returns {number}
 */
export const getChatMessageCount = () => {
  try {
    const raw = sessionStorage.getItem('jj_chat_message_count');
    const parsed = parseInt(raw == null ? '0' : raw, 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  } catch (error) {
    logger.error('Failed to get chat message count', { error: error.message });
    return 0;
  }
};

/**
 * Set the chat message counter
 * @param {number} value
 */
export const setChatMessageCount = (value) => {
  try {
    const num = Number.isFinite(value) && value >= 0 ? Math.floor(value) : 0;
    sessionStorage.setItem('jj_chat_message_count', String(num));
  } catch (error) {
    logger.error('Failed to set chat message count', { error: error.message });
  }
};

/**
 * Increment the chat message counter
 * @returns {number} The new count
 */
export const incrementChatMessageCount = () => {
  try {
    const next = getChatMessageCount() + 1;
    sessionStorage.setItem('jj_chat_message_count', String(next));
    return next;
  } catch (error) {
    logger.error('Failed to increment chat message count', { error: error.message });
    return 0;
  }
};

/**
 * Reset the chat message counter to 0
 */
export const resetChatMessageCount = () => {
  try {
    sessionStorage.setItem('jj_chat_message_count', '0');
  } catch (error) {
    logger.error('Failed to reset chat message count', { error: error.message });
  }
};

// ===== PROFILE FUNCTIONS =====

/**
 * Get profile data from sessionStorage
 * @returns {Object} Profile data object with organized facts
 */
export const getProfileData = () => {
  try {
    return {
      firstname: sessionStorage.getItem('profile_facts_firstname') || '',
      lastname: sessionStorage.getItem('profile_facts_lastname') || '',
      gender: sessionStorage.getItem('profile_facts_gender') || 'Not Specified',
      form_of_address: sessionStorage.getItem('profile_facts_form_of_address') || '',
      personal_info: JSON.parse(sessionStorage.getItem('profile_facts_personal_info') || '[]'),
      learned_information: JSON.parse(sessionStorage.getItem('profile_facts_learned_information') || '[]'),
      unsaved_changes: sessionStorage.getItem('profile_unsaved_changes') === 'true',
      editing_fact_id: sessionStorage.getItem('profile_editing_fact_id') || null
    };
  } catch (error) {
    logger.error('Failed to get profile data', { error: error.message });
    return {
      firstname: '',
      lastname: '',
      gender: 'Not Specified',
      form_of_address: '',
      personal_info: [],
      learned_information: [],
      unsaved_changes: false,
      editing_fact_id: null
    };
  }
};

/**
 * Set profile fact in sessionStorage
 * @param {string} type - Type of fact (firstname, lastname, gender, form_of_address, personal_info)
 * @param {string|Array} value - Value to store
 */
export const setProfileFact = (type, value) => {
  try {
    const key = `profile_facts_${type}`;
    if (type === 'personal_info' || type === 'learned_information') {
      sessionStorage.setItem(key, JSON.stringify(value));
    } else {
      sessionStorage.setItem(key, value);
    }
    logger.debug('Profile fact set', { type, key });
  } catch (error) {
    logger.error('Failed to set profile fact', { type, error: error.message });
  }
};

/**
 * Set profile unsaved changes flag
 * @param {boolean} hasChanges - Whether there are unsaved changes
 */
export const setProfileUnsavedChanges = (hasChanges) => {
  try {
    sessionStorage.setItem('profile_unsaved_changes', hasChanges ? 'true' : 'false');
  } catch (error) {
    logger.error('Failed to set profile unsaved changes flag', { error: error.message });
  }
};

/**
 * Set profile editing fact ID
 * @param {string|null} factId - ID of fact being edited
 */
export const setProfileEditingFactId = (factId) => {
  try {
    if (factId) {
      sessionStorage.setItem('profile_editing_fact_id', factId);
    } else {
      sessionStorage.removeItem('profile_editing_fact_id');
    }
  } catch (error) {
    logger.error('Failed to set profile editing fact ID', { error: error.message });
  }
};

/**
 * Clear all profile data from sessionStorage
 */
export const clearProfileData = () => {
  try {
    const keys = [
      'profile_facts_firstname', 
      'profile_facts_lastname', 
      'profile_facts_gender',
      'profile_facts_form_of_address', 
      'profile_facts_personal_info',
      'profile_facts_learned_information',
      'profile_unsaved_changes', 
      'profile_editing_fact_id'
    ];
    keys.forEach(key => sessionStorage.removeItem(key));
    logger.info('Profile data cleared from sessionStorage');
  } catch (error) {
    logger.error('Failed to clear profile data', { error: error.message });
  }
};

/**
 * Get profile facts organized by type from sessionStorage
 * @returns {Object} Facts organized by type
 */
export const getProfileFactsByType = () => {
  try {
    const profileData = getProfileData();
    return {
      firstname: profileData.firstname ? [{ fact: profileData.firstname, type: 'firstname' }] : [],
      lastname: profileData.lastname ? [{ fact: profileData.lastname, type: 'lastname' }] : [],
      gender: profileData.gender ? [{ fact: profileData.gender, type: 'gender' }] : [],
      form_of_address: profileData.form_of_address ? [{ fact: profileData.form_of_address, type: 'form_of_address' }] : [],
      personal_info: profileData.personal_info || [],
      learned_information: profileData.learned_information || []
    };
  } catch (error) {
    logger.error('Failed to get profile facts by type', { error: error.message });
    return {
      firstname: [],
      lastname: [],
      gender: [],
      form_of_address: [],
      personal_info: [],
      learned_information: []
    };
  }
};

/**
 * Update profile data with new facts from API response
 * @param {Array} facts - Array of facts from API
 */
export const updateProfileDataFromFacts = (facts) => {
  try {
    const organizedData = {
      firstname: '',
      lastname: '',
      gender: 'Not Specified',
      form_of_address: '',
      personal_info: [],
      learned_information: []
    };

    facts.forEach(fact => {
      switch (fact.type) {
        case 'firstname':
          organizedData.firstname = fact.fact;
          break;
        case 'lastname':
          organizedData.lastname = fact.fact;
          break;
        case 'gender':
          organizedData.gender = fact.fact;
          break;
        case 'form_of_address':
          organizedData.form_of_address = fact.fact;
          break;
        case 'personal_info':
          if (fact.source === 'agent') {
            organizedData.learned_information.push(fact);
          } else {
            organizedData.personal_info.push(fact);
          }
          break;
        default:
          logger.warn('Unknown profile fact type', { type: fact.type });
      }
    });

    // Update sessionStorage
    Object.keys(organizedData).forEach(key => {
      setProfileFact(key, organizedData[key]);
    });

    logger.info('Profile data updated from facts', { factCount: facts.length });
  } catch (error) {
    logger.error('Failed to update profile data from facts', { error: error.message });
  }
};

/**
 * Assemble known facts from user profile data for user fact detection
 * @returns {string[]} Array of known facts from both personal_info and learned_information
 */
export const getKnownFactsForDetection = () => {
  try {
    const profileData = getProfileData();
    const knownFacts = [];
    
    // Add personal information facts
    if (profileData.personal_info && Array.isArray(profileData.personal_info)) {
      profileData.personal_info.forEach(fact => {
        if (fact.fact && fact.fact.trim()) {
          knownFacts.push(fact.fact.trim());
        }
      });
    }
    
    // Add learned information facts
    if (profileData.learned_information && Array.isArray(profileData.learned_information)) {
      profileData.learned_information.forEach(fact => {
        if (fact.fact && fact.fact.trim()) {
          knownFacts.push(fact.fact.trim());
        }
      });
    }
    
    logger.debug('Assembled known facts for detection', { 
      personalInfoCount: profileData.personal_info?.length || 0,
      learnedInfoCount: profileData.learned_information?.length || 0,
      totalKnownFacts: knownFacts.length
    });
    
    return knownFacts;
  } catch (error) {
    logger.error('Failed to assemble known facts for detection', { error: error.message });
    return [];
  }
};

// ===== CURRENT SUBJECT FUNCTIONS =====

/**
 * Get current subject from sessionStorage
 * @returns {string} Current subject or 'none' if not found
 */
export const getCurrentSubject = () => {
  try {
    const subject = sessionStorage.getItem('currentSubject');
    return subject || 'none';
  } catch (error) {
    logger.error('Failed to get current subject', { error: error.message });
    return 'none';
  }
};

/**
 * Set current subject in sessionStorage
 * @param {string} subject - Subject to store
 */
export const setCurrentSubject = (subject) => {
  try {
    if (subject) {
      sessionStorage.setItem('currentSubject', subject);
    } else {
      sessionStorage.removeItem('currentSubject');
    }
  } catch (error) {
    logger.error('Failed to set current subject', { error: error.message });
  }
};

/**
 * Remove current subject from sessionStorage
 */
export const removeCurrentSubject = () => {
  try {
    sessionStorage.removeItem('currentSubject');
  } catch (error) {
    logger.error('Failed to remove current subject', { error: error.message });
  }
};

/**
 * Get subject prompt text from sessionStorage
 * @returns {string} Subject prompt text or empty string if not found
 */
export const getSubjectPromptText = () => {
  try {
    const promptText = sessionStorage.getItem('subjectPromptText');
    return promptText || '';
  } catch (error) {
    logger.error('Failed to get subject prompt text', { error: error.message });
    return '';
  }
};

/**
 * Get subject prompt MCP usage flag from sessionStorage
 * @returns {boolean} Whether MCP agents are used for current subject
 */
export const getSubjectPromptAgentsUsed = () => {
  try {
    const value = sessionStorage.getItem('subjectPromptAgentsUsed');
    if (value === null || value === undefined) {
      return false;
    }
    return value === 'true';
  } catch (error) {
    logger.error('Failed to get subject prompt agents_used', { error: error.message });
    return false;
  }
};

/**
 * Set subject prompt text in sessionStorage
 * @param {string} promptText - Subject prompt text to store
 */
export const setSubjectPromptText = (promptText) => {
  try {
    if (promptText) {
      sessionStorage.setItem('subjectPromptText', promptText);
    } else {
      sessionStorage.removeItem('subjectPromptText');
    }
  } catch (error) {
    logger.error('Failed to set subject prompt text', { error: error.message });
  }
};

/**
 * Set subject prompt MCP usage flag in sessionStorage
 * @param {boolean} agentsUsed - Whether MCP agents are used for current subject
 */
export const setSubjectPromptAgentsUsed = (agentsUsed) => {
  try {
    sessionStorage.setItem('subjectPromptAgentsUsed', agentsUsed ? 'true' : 'false');
  } catch (error) {
    logger.error('Failed to set subject prompt agents_used', { error: error.message });
  }
};

/**
 * Remove subject prompt text from sessionStorage
 */
export const removeSubjectPromptText = () => {
  try {
    sessionStorage.removeItem('subjectPromptText');
  } catch (error) {
    logger.error('Failed to remove subject prompt text', { error: error.message });
  }
};

/**
 * Remove subject prompt MCP usage flag from sessionStorage
 */
export const removeSubjectPromptAgentsUsed = () => {
  try {
    sessionStorage.removeItem('subjectPromptAgentsUsed');
  } catch (error) {
    logger.error('Failed to remove subject prompt agents_used', { error: error.message });
  }
};

// ===== ENABLE SUBJECT TRACKING FUNCTIONS =====

/**
 * Get enable subject tracking flag from sessionStorage
 * @returns {boolean} Enable subject tracking flag, defaults to true if not set
 */
export const getEnableSubjectTracking = () => {
  try {
    const value = sessionStorage.getItem('enable_current_subject_tracking');
    if (value === null || value === undefined) {
      return true; // Default to enabled for backward compatibility
    }
    return value === 'true';
  } catch (error) {
    logger.error('Failed to get enable subject tracking', { error: error.message });
    return true; // Default to enabled on error
  }
};

/**
 * Set enable subject tracking flag in sessionStorage
 * @param {boolean} enabled - Whether subject tracking is enabled
 */
export const setEnableSubjectTracking = (enabled) => {
  try {
    if (enabled) {
      sessionStorage.setItem('enable_current_subject_tracking', 'true');
    } else {
      sessionStorage.setItem('enable_current_subject_tracking', 'false');
    }
  } catch (error) {
    logger.error('Failed to set enable subject tracking', { error: error.message });
  }
};

/**
 * Remove enable subject tracking flag from sessionStorage
 */
export const removeEnableSubjectTracking = () => {
  try {
    sessionStorage.removeItem('enable_current_subject_tracking');
  } catch (error) {
    logger.error('Failed to remove enable subject tracking', { error: error.message });
  }
};

// ===== LOCK CURRENT SUBJECT FUNCTIONS =====

/**
 * Get lock current subject flag from sessionStorage
 * @returns {boolean} Lock current subject flag, defaults to false if not set (unchecked by default)
 */
export const getLockCurrentSubject = () => {
  try {
    const value = sessionStorage.getItem('lock_current_subject');
    if (value === null || value === undefined) {
      return false; // Default to unlocked (unchecked) by default
    }
    return value === 'true';
  } catch (error) {
    logger.error('Failed to get lock current subject', { error: error.message });
    return false; // Default to unlocked on error
  }
};

/**
 * Set lock current subject flag in sessionStorage
 * @param {boolean} locked - Whether subject is locked
 */
export const setLockCurrentSubject = (locked) => {
  try {
    if (locked) {
      sessionStorage.setItem('lock_current_subject', 'true');
    } else {
      sessionStorage.setItem('lock_current_subject', 'false');
    }
  } catch (error) {
    logger.error('Failed to set lock current subject', { error: error.message });
  }
};

/**
 * Remove lock current subject flag from sessionStorage
 */
export const removeLockCurrentSubject = () => {
  try {
    sessionStorage.removeItem('lock_current_subject');
  } catch (error) {
    logger.error('Failed to remove lock current subject', { error: error.message });
  }
};